import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Search, ChevronDown } from "lucide-react";
import { glossaryCategories } from "../mock/glossary";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

export default function Glossary() {
  const location = useLocation();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [openSlug, setOpenSlug] = useState(location.state?.open || null);
  const { data: glossaryTerms, loading } = useFetch("/glossary");

  useEffect(() => {
    if (location.state?.open) {
      const el = document.getElementById(`term-${location.state.open}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [location.state]);

  const shown = (glossaryTerms || []).filter((t) => (cat === "All" || t.category === cat) && (t.term + t.short).toLowerCase().includes(q.toLowerCase()));

  return (
    <main className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14" data-testid="glossary-page">
      <SectionLabel>Live · updated from trending searches</SectionLabel>
      <h1 className="font-display font-bold text-[38px] lg:text-[54px] tracking-tight">The Risk <span className="text-[#3F4CE0]">Glossary</span></h1>
      <p className="mt-4 text-[16px] text-[#1A1914]/60 max-w-2xl">{(glossaryTerms || []).length} of 212+ terms shown in preview — across ERM, cyber, operational, financial, ESG, AI, audit, supply chain and government risk.</p>
      <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="relative w-full md:w-96">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1A1914]/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…" data-testid="glossary-search"
            className="w-full pl-10 pr-4 py-3 text-[14px] bg-white border border-[#E7E0D2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40 focus:border-[#3F4CE0]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {glossaryCategories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`glossary-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3.5 py-1.5 text-[13px] font-medium rounded-full border rp-btn ${cat === c ? "bg-[#1A1914] text-white border-[#1A1914]" : "bg-white text-[#1A1914]/70 border-[#E7E0D2] hover:border-[#1A1914]/40"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="glossary-grid">
        {shown.map((t) => {
          const open = openSlug === t.slug;
          return (
            <div key={t.slug} id={`term-${t.slug}`} className={`rp-card bg-white border rounded-2xl p-5 cursor-pointer ${open ? "border-[#3F4CE0]/50 ring-1 ring-[#3F4CE0]/20" : "border-[#E7E0D2]"}`}
              onClick={() => setOpenSlug(open ? null : t.slug)} data-testid={`glossary-term-${t.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display font-semibold text-[19px] leading-snug">{t.term}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono-label text-[9.5px] px-2 py-0.5 rounded-full bg-[#F3EEE4] text-[#1A1914]/55 border border-[#E7E0D2]">{t.category}</span>
                  <ChevronDown size={15} className={`text-[#1A1914]/40 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                </div>
              </div>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#1A1914]/60">{t.short}</p>
              {open && (
                <div className="mt-4 pt-4 border-t border-[#E7E0D2] rp-fade-up" style={{ animationDuration: "0.3s" }}>
                  <p className="font-mono-label text-[9.5px] text-[#3F4CE0] mb-2">Full breakdown</p>
                  <p className="text-[14px] leading-relaxed text-[#1A1914]/75">{t.detail}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {shown.length === 0 && !loading && <p className="mt-12 text-[15px] text-[#1A1914]/50">No terms match — try another search.</p>}
    </main>
  );
}
